import React, { useRef, useState } from "react";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { useShallow } from "zustand/react/shallow";
import { MetaAddButton } from "@/components/main/datasets/DatasetMetaSection";
import { ContextMenu } from "@/components/common/ContextMenu";
import { useCatalogCreateMenu } from "@/components/main/hooks/useCatalogCreateMenu";
import { resolveDatasetColor } from "@/lib/datasetColor";
import {
  spaceColor,
  useOrgContextStore,
} from "@/components/main/stores/orgContextStore";
import { SpacePickerMenu } from "@/components/main/spaces/SpacePickerMenu";

interface CreateModelStripProps {
  /// Org-wide, not the filtered bucket: the button only pulses for an account
  /// that has never made a model.
  hasModels: boolean;
  onCreateModel: () => void;
  /// Create a role-owned catalog model. Only reachable by right-clicking the
  /// pill, and only for catalog managers.
  onCreateCatalogModel: () => void;
}

/// The Overview's create action: "New model", the space it lands in, and the
/// catalog variant behind a right-click.
export const CreateModelStrip: React.FC<CreateModelStripProps> = ({
  hasModels,
  onCreateModel,
  onCreateCatalogModel,
}) => {
  const catalogMenu = useCatalogCreateMenu();
  const pickerAnchorRef = useRef<HTMLButtonElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { setCreateSpace, spaceById, activeOrg } = useOrgContextStore(
    useShallow((s) => ({
      activeOrg: s.activeOrg(),
      setCreateSpace: s.setCreateSpace,
      spaceById: s.spaceById,
    })),
  );
  const createSpaceId = useOrgContextStore((s) => s.createSpaceId());
  const createSpace = spaceById(createSpaceId);
  // The door refuses without a live space, so the affordance says so instead
  // of failing after the click. A space in its deletion window is not one
  // (D52: it is in the Trash), and an org on its way out takes nothing new.
  const scheduledOrg = activeOrg?.deletion_scheduled_at ? activeOrg : undefined;
  const cannotCreate = !createSpace || !!scheduledOrg;
  const refusalReason = scheduledOrg
    ? `${scheduledOrg.name} is scheduled for deletion — it takes no new models until that is cancelled.`
    : createSpace
      ? undefined
      : "Create a space first — models live in spaces.";
  // A dead control must not light up under the cursor — the whole joined shape
  // stays grey while the space it would create in is on its way out.
  const groupAccent = cannotCreate
    ? ""
    : " group-hover:border-accent group-hover:text-accent";

  return (
    <div className="flex items-center justify-end gap-2 mt-8">
      <span
        className={`group flex items-stretch${
          !hasModels && !cannotCreate ? " pulse-hint" : ""
        }`}
      >
        <MetaAddButton
          label="New model"
          onClick={onCreateModel}
          disabled={cannotCreate}
          title={refusalReason}
          menuTriggerProps={catalogMenu.triggerProps}
          className={
            createSpace ? `rounded-r-none border-r-0${groupAccent}` : ""
          }
        />
        {createSpace && (
          <>
            <button
              ref={pickerAnchorRef}
              onClick={() => setPickerOpen((v) => !v)}
              aria-label={`Create in ${createSpace.name} — change space`}
              aria-expanded={pickerOpen}
              className={`flex min-h-[var(--control-height-sm)] items-center gap-1.5 rounded-app rounded-l-none border border-l-0 border-dashed border-border bg-list-hover px-2 text-small text-fg-muted transition-colors${groupAccent}`}
            >
              in
              <span
                className={`flex min-w-0 items-center gap-1.5 font-medium text-fg transition-colors${
                  cannotCreate ? "" : " group-hover:text-accent"
                }`}
              >
                <span
                  aria-hidden
                  className="h-1.5 w-1.5 shrink-0 rounded-app"
                  style={{
                    backgroundColor: resolveDatasetColor(
                      spaceColor(createSpace),
                    ),
                  }}
                />
                <span className="max-w-40 truncate">{createSpace.name}</span>
              </span>
              <UnfoldMoreIcon sx={{ fontSize: 12 }} className="shrink-0" />
            </button>
            <SpacePickerMenu
              anchorRef={pickerAnchorRef}
              isOpen={pickerOpen}
              onClose={() => setPickerOpen(false)}
              selectedSpaceId={createSpaceId}
              onPick={setCreateSpace}
            />
          </>
        )}
      </span>
      <ContextMenu
        ariaLabel="Create a model"
        position={catalogMenu.position}
        onClose={catalogMenu.close}
        items={[
          {
            label: "New model",
            icon: <AddOutlinedIcon className="icon-xs" />,
            onClick: onCreateModel,
            disabled: cannotCreate,
            hint: cannotCreate
              ? scheduledOrg
                ? "organization is being deleted"
                : "no space to create in"
              : undefined,
          },
          {
            label: "New catalog model",
            icon: <PublicOutlinedIcon className="icon-xs" />,
            onClick: onCreateCatalogModel,
          },
        ]}
      />
    </div>
  );
};
